import React, { useState, useCallback } from 'react';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import { ProfileAnimatedPoints } from 'components/Points/ProfileAnimatedPoints';
import MagicButton from 'components/MagicButton/MagicButton';
import { PublicUserProfile } from 'types/interfaces/profile';
import { Skeleton } from '@mui/material';
import { AssetUrls } from 'config/assets';
import { toast } from 'react-toastify';
import { SocialLinksSection } from '../SocialLinksSection/SocialLinksSection';
import { linkifyText } from 'utils/urlDetection';
import EliteBadge from 'components/EliteBadge/EliteBadge';
import './ProfileCard.css';

interface ProfileCardProps {
  profile: PublicUserProfile | undefined;
  isLoading: boolean;
  isOwnProfile: boolean;
  showContactButton: boolean;
  onContactClick: () => void;
  onSendPointsClick: () => void;
  onSendCardsClick: () => void;
  isViewerElite?: boolean;
}

// Format location string based on country
const formatLocation = (city: string | null, state: string | null, country: string | null): string | null => {
  const isUS = country?.toUpperCase() === 'USA' || country?.toUpperCase() === 'US';

  if (isUS) {
    // US: "City, State"
    if (city && state) return `${city}, ${state}`;
    if (city) return city;
    if (state) return state;
    return null;
  } else {
    // International: "City, Country"
    if (city && country) return `${city}, ${country}`;
    if (city) return city;
    if (country) return country;
    return null;
  }
};

// Format join date as "Trading since Month Year"
const formatJoinDate = (createdAt: string): string => {
  const date = new Date(createdAt);
  const month = date.toLocaleString('en-US', { month: 'long' });
  const year = date.getFullYear();
  return `Trading since ${month} ${year}`;
};

// Render star rating
const StarRating: React.FC<{ rating: number | null; count: number }> = ({ rating, count }) => {
  const displayRating = rating ?? 0;
  const fullStars = Math.floor(displayRating);
  const hasHalfStar = displayRating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <div className="profile-card-rating">
      <div className="profile-card-stars">
        {[...Array(fullStars)].map((_, i) => (
          <span key={`full-${i}`} className="star full">&#9733;</span>
        ))}
        {hasHalfStar && <span className="star half">&#9733;</span>}
        {[...Array(emptyStars)].map((_, i) => (
          <span key={`empty-${i}`} className="star empty">&#9734;</span>
        ))}
      </div>
      <span className="profile-card-rating-count">({count} ratings)</span>
    </div>
  );
};

const ShareProfileButton: React.FC<{ profile: PublicUserProfile }> = ({ profile }) => {
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback(async () => {
    const url = profile.customSlug
      ? `https://trademagic.gg/u/${profile.customSlug}`
      : `https://trademagic.gg/profile/u/${profile.username}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Profile link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  }, [profile.customSlug, profile.username]);

  return (
    <button
      className="profile-card-share-btn"
      onClick={handleShare}
      title="Copy profile link"
      aria-label="Share profile"
    >
      {copied ? (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M3 8.5L6.5 12L13 4" stroke="#4caf50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M10 2H14V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M14 2L8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 9V13C12 13.55 11.55 14 11 14H3C2.45 14 2 13.55 2 13V5C2 4.45 2.45 4 3 4H7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
};

export const ProfileCard: React.FC<ProfileCardProps> = ({
  profile,
  isLoading,
  isOwnProfile,
  showContactButton,
  onContactClick,
  onSendPointsClick,
  onSendCardsClick,
  isViewerElite = false,
}) => {
  if (isLoading) {
    return (
      <div className="profile-card profile-card--loading">
        <Skeleton variant="circular" width={120} height={120} sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
        <Skeleton variant="text" width={150} height={32} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', mt: 2 }} />
        <Skeleton variant="text" width={100} height={24} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', mt: 1 }} />
        <Skeleton variant="text" width={80} height={24} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', mt: 1 }} />
        <Skeleton variant="text" width={120} height={20} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', mt: 1 }} />
        <Skeleton variant="text" width={160} height={20} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', mt: 1 }} />
        <Skeleton variant="rectangular" width="100%" height={60} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', mt: 2, borderRadius: 2 }} />
        <div className="profile-card-buttons">
          <Skeleton variant="rectangular" width="48%" height={44} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 2 }} />
          <Skeleton variant="rectangular" width="48%" height={44} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 2 }} />
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const location = formatLocation(profile.city, profile.state, profile.country);

  return (
    <div className="profile-card">
      <div className="profile-card-avatar-section">
        <AvatarWithFlag
          avatarUrl={profile.avatarUrl}
          username={profile.username}
          countryCode={profile.country}
          size={120}
          isElite={profile.isElite}
          showAnimatedBorder={profile.showAnimatedBorder}
          borderColor1={profile.borderColor1}
          borderColor2={profile.borderColor2}
        />
        <ProfileAnimatedPoints points={profile.points} playSound={false} />
      </div>

      <div className="profile-card-username-row">
        <h1 className="profile-card-username">{profile.username}<EliteBadge isElite={!!profile.isElite} /></h1>
        <ShareProfileButton profile={profile} />
      </div>

      <StarRating rating={profile.averageRating} count={profile.totalRatings} />

      {location && (
        <div className="profile-card-location">
          <span className="profile-card-location-pin">📍</span>
          <span>{location}</span>
        </div>
      )}

      <div className="profile-card-join-date">
        {formatJoinDate(profile.createdAt)}
      </div>

      {profile.bio && (
        <div className="profile-card-bio">
          {profile.isElite ? linkifyText(profile.bio) : profile.bio}
        </div>
      )}

      {!isOwnProfile && (
        <div className="profile-card-buttons">
          <div className="profile-card-buttons-row">
            {showContactButton && (
              <MagicButton
                label="Contact"
                color="dark-grey"
                icon={
                  <img
                    src={`${AssetUrls.base}/assets/icons/contact.svg`}
                    alt="Contact"
                    style={{ width: 16, height: 16 }}
                  />
                }
                onPress={onContactClick}
              />
            )}
            <MagicButton
              label="Send Points"
              color="dark-grey"
              icon={
                <img
                  src={AssetUrls.icons.profile.coin}
                  alt="Points"
                  style={{ width: 16, height: 16 }}
                />
              }
              onPress={onSendPointsClick}
            />
          </div>
          <MagicButton
            label="Send Cards"
            color="purple"
            icon={
              <img
                src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/logos/arevakhach-icon.svg"
                alt="Send"
                style={{ width: 16, height: 16 }}
              />
            }
            onPress={onSendCardsClick}
          />
        </div>
      )}

      <SocialLinksSection
        username={profile.username}
        isOwnProfile={isOwnProfile}
        isViewerElite={isViewerElite}
      />
    </div>
  );
};

export default ProfileCard;
